import type { JSX } from 'react';
import api from '@/api';
import isEmpty from 'lodash/isEmpty';
import { ServiceSupportedCustomWeaponList, Weapon } from '@/api/types';
import React, { PropsWithChildren } from 'react';
import { NullValueGuard } from '@/middlewares/NullValueGuard';

import { Accordion, ConditionView, Typography } from '@/shared/ui';

import { formatDeadlineCountdown } from '@/modules/erz/helpers/formatDeadlineCountdown';
import { formatDate } from '@/helpers/dates';

export const WEAPON_PERMIT_TYPE_TEXT =
  'дозвіл на зберігання, носіння вогнепальної мисливської зброї, основних частин зброї, ' +
  'пневматичної, холодної, охолощеної зброї, пристроїв';

export const Placeholder: React.FC<PropsWithChildren> = ({ children }) => (
  <NullValueGuard placeholder="Не вказано">{children as any}</NullValueGuard>
);

export const renderWeapon = (weapon: Weapon): JSX.Element => {
  const permit = weapon?.EXTRA?.Permit?.[0];
  const weaponModel = weapon.weaponType === 'firearm_part' ? (weapon.ORNM ?? null) : (weapon.ORMD ?? null);
  const weaponManufacturer = weapon.weaponType !== 'firearm_part' ? (weapon.ORIZ ?? null) : null;
  return (
    <Accordion
      variant="transparent"
      items={[
        {
          label: (
            <div className="weapon__header">
              <Typography color="graphite" variant="TEXT14">
                {weapon.ORVD}
              </Typography>
              <Typography variant="H4">{weaponModel}</Typography>
            </div>
          ),
          children: (
            <div className="weapon__body">
              <Typography color="graphite" variant="TEXT14">
                Деталі:
              </Typography>
              <ul className="weapon__details">
                <Typography as="li">
                  Серія та номер: <Placeholder>{weapon.ORSR}</Placeholder>
                </Typography>
                <Typography as="li">
                  Виробник: <Placeholder>{weaponManufacturer}</Placeholder>
                </Typography>
                <Typography as="li">
                  Ствол: <Placeholder>{weapon.ORKL}</Placeholder>
                </Typography>
                <ConditionView if={!isEmpty(weapon.EXTRA?.purchaseOperation)}>
                  <Typography as="li">Дата купівлі:</Typography>
                  <ConditionView if={!!weapon.EXTRA?.purchaseOperation?.nextOperationDeadline}>
                    <Typography as="li">
                      Отримати дозвіл до: {formatDate(weapon.EXTRA?.purchaseOperation?.nextOperationDeadline)}
                    </Typography>
                  </ConditionView>
                  <ConditionView if={!!weapon.EXTRA?.purchaseOperation?.secondsLeft}>
                    <Typography as="li">
                      Залишилось: {formatDeadlineCountdown(weapon.EXTRA?.purchaseOperation?.secondsLeft as number)}
                    </Typography>
                  </ConditionView>
                </ConditionView>
                <Typography as="li">Тип дозволу: {WEAPON_PERMIT_TYPE_TEXT}</Typography>
                <Typography as="li">
                  Номер дозволу: <Placeholder>{permit?.number}</Placeholder>
                </Typography>
                <Typography as="li">
                  Дата видачі: <Placeholder>{permit?.issuedAt && formatDate(permit.issuedAt)}</Placeholder>
                </Typography>
                <Typography as="li">
                  Термін дії: <Placeholder>{permit?.expiredAt && formatDate(permit.expiredAt)}</Placeholder>
                </Typography>
              </ul>
            </div>
          ),
        },
      ]}
    />
  );
};

export const loadWeaponDataFn = async (serviceCode: ServiceSupportedCustomWeaponList) => {
  try {
    const [{ data: weapons }, { data: npu = [] }] = await Promise.all([
      api.services.serviceWeapons(serviceCode),
      api.dictionary.getAuthorities(),
    ]);
    return {
      weapons: weapons.ipsItems,
      orgNpuOptions: npu.map((item) => ({ label: item.name, id: item.id, isPayable: item.isPayable })),
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
};
